import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { TournamentSchedule } from '../components/TournamentSchedule';
import { DetailSkeleton } from '../components/PageSkeletons';
import axios from 'axios';
import { toast } from 'sonner';
import { hapticSuccess } from '../native';
import {
  ShieldCheck, ArrowLeft, Calendar, Users, Layout, UserCheck, Play,
  Lock, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ['upcoming', 'active', 'completed'];

const statusColor = (status) => {
  switch (status) {
    case 'upcoming': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
    case 'completed': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    default: return '';
  }
};

const ManageLink = ({ to, icon: Icon, title, description }) => (
  <Link to={to}>
    <div className="flex items-center gap-4 p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors">
      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
    </div>
  </Link>
);

export const TournamentDirector = () => {
  const { tournamentId } = useParams();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchTournament = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tournaments/${tournamentId}`);
      setTournament(res.data);
    } catch (error) {
      console.error('Failed to fetch tournament:', error);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  const isDirector = tournament && user && (tournament.created_by === user.id || user.role === 'admin');

  const changeStatus = async (status) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/tournaments/${tournamentId}/status`, { status });
      hapticSuccess();
      toast.success(`Tournament marked as ${status}`);
      setTournament((prev) => ({ ...prev, status }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DetailSkeleton />
      </div>
    );
  }

  if (!tournament) {
    return null;
  }

  if (!isDirector) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="director-not-authorized">
        <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
        <h1 className="text-xl font-bold mb-2">Director access only</h1>
        <p className="text-muted-foreground mb-6">
          Only {tournament.name}'s organizer or a site admin can open its Director Panel.
        </p>
        <Link to={`/tournaments/${tournamentId}`}>
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Tournament
          </Button>
        </Link>
      </div>
    );
  }

  const dateRange = tournament.start_date === tournament.end_date
    ? new Date(`${tournament.start_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : `${new Date(`${tournament.start_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(`${tournament.end_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tournament-director-page">
      <Link to={`/tournaments/${tournamentId}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to Tournament
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-primary">Director Panel</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black">{tournament.name}</h1>
          <p className="text-muted-foreground text-sm mt-1 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {dateRange}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={statusColor(tournament.status)}>
            {tournament.status?.toUpperCase()}
          </Badge>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Tournament Status</CardTitle>
          <CardDescription>Controls what the public sees on the tournament page</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((status) => (
            <Button
              key={status}
              size="sm"
              variant={tournament.status === status ? 'default' : 'outline'}
              disabled={updatingStatus || tournament.status === status}
              onClick={() => changeStatus(status)}
              data-testid={`status-btn-${status}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Manage</CardTitle>
          <CardDescription>Backend controls scoped to this tournament only</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ManageLink
            to={`/tournaments/${tournamentId}/bracket-builder`}
            icon={Layout}
            title="Bracket Builder"
            description="Generate and seed the elimination bracket"
          />
          <ManageLink
            to={`/tournaments/${tournamentId}/pools`}
            icon={Play}
            title="Pool Play"
            description="Set up pools and round-robin games"
          />
          <ManageLink
            to={`/tournaments/${tournamentId}/referees`}
            icon={UserCheck}
            title="Referee Assignment"
            description="Assign officials to games"
          />
          <ManageLink
            to={`/tournaments/${tournamentId}`}
            icon={Users}
            title="Registered Teams"
            description={`${tournament.registered_teams || 0} of ${tournament.max_teams} teams`}
          />
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-bold mb-3">Schedule</h2>
        <TournamentSchedule tournamentId={tournamentId} editable />
      </div>
    </div>
  );
};
